import type { PaginationState } from '@tanstack/react-table'
import type { AxiosRequestConfig } from 'axios'
import type {
  LoginFormValues,
  PostLoginRequest,
} from '~/modules/login/login-form/constants'

import type {
  PostRegisterRequest,
  RegisterFormValues,
} from '~/modules/register/register-form/constants'
import type {
  PostVerifyRequest,
  VerifyFormValues,
} from '~/modules/verify/verify-form/constants'
import { getRequest, postRequest, putRequest } from '@lib/util/http'

import { BACKEND_URL } from '~/lib/config'
import { authController } from './consts'

export const usersController = 'users/admin-users'
export const assignableUsersSearchController = 'users/assignable-users/search'

export interface AssignableUser {
  id: string
  name?: string | null
  email?: string | null
}

export interface AssignableUsersSearchResponse {
  users: AssignableUser[]
}

export const LOGIN_ERROR_CODE = 333

export enum ROLE {
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
}

export type AdminUserRole
  = | 'CUSTOMER'
    | 'COMMERCIAL'
    | 'FINANCIAL'
    | 'DIRECTOR'
    | 'ADMINISTRATOR'
    | 'DESIGNER'
    | 'EXTERNAL_DESIGNER'

export type AssignableAdminUserRole
  = | 'CUSTOMER'
    | 'COMMERCIAL'
    | 'FINANCIAL'
    | 'DIRECTOR'
    | 'ADMINISTRATOR'

export interface AdminUser {
  uuid: string
  name: string | null
  email: string | null
  role: AdminUserRole
}

export interface AdminUsersResponseData {
  users: AdminUser[]
  current_page: number
  total_pages: number
  pageIndex: number
  pageSize: number
}

export interface AdminUsersResponse {
  data: AdminUsersResponseData
}

export interface AdminUserResponse {
  data: AdminUser
}

export interface AdminUsersQueryFilters {
  pagination?: PaginationState
  query?: string
  employees?: boolean
}

export interface AdminUserUpdatePayload {
  name: string | null
  role?: AssignableAdminUserRole
}

export async function getAssignableUsersSearch(
  query = '',
  axiosConfig: AxiosRequestConfig = {},
) {
  const fetchURL = new URL(assignableUsersSearchController, BACKEND_URL)
  const trimmedQuery = query.trim()
  if (trimmedQuery) {
    fetchURL.searchParams.set('query', trimmedQuery)
  }
  return getRequest<AssignableUsersSearchResponse>(fetchURL.href, axiosConfig)
}

export async function getAdminUsers(
  queryFilters?: AdminUsersQueryFilters,
  axiosConfig: AxiosRequestConfig = {},
) {
  const fetchURL = new URL(usersController, BACKEND_URL)
  const { pagination, query, employees } = queryFilters || {}

  if (pagination) {
    fetchURL.searchParams.set('pagination', JSON.stringify(pagination))
  }

  const trimmedQuery = query?.trim()
  if (trimmedQuery) {
    fetchURL.searchParams.set('query', trimmedQuery)
  }

  if (employees) {
    fetchURL.searchParams.set('employees', 'true')
  }

  return getRequest<AdminUsersResponse>(fetchURL.href, axiosConfig)
}

export async function updateAdminUser(
  userUuid: string,
  payload: AdminUserUpdatePayload,
) {
  return putRequest<AdminUserResponse, AdminUserUpdatePayload>(
    `${usersController}/${userUuid}`,
    payload,
  )
}

export interface PostLoginResponseSuccess {
  data: {
    email: string
    userName: string
    accessToken: string
    refreshToken: string
    role: string
    permissions: string[]
  }
}

export type PostLoginResponse = PostLoginResponseSuccess | APIError

export interface PostGoogleLoginRequest {
  credential: string
}

export type PostGoogleLoginResponse = PostLoginResponse

export type PostLoginPayload = LoginFormValues

export type PostLoginQuery = (
  newData: PostLoginPayload,
) => Promise<PostLoginResponse>

export async function postLogin(UserPayload: PostLoginRequest) {
  return postRequest<PostLoginResponse, PostLoginRequest>(
    `${authController}/login`,
    UserPayload,
    {
      headers: {
        Authorization: `Basic ${btoa(`${UserPayload.email}:${UserPayload.password}`)}`,
      },
    },
  )
}

export async function postGoogleLogin(payload: PostGoogleLoginRequest) {
  return postRequest<PostGoogleLoginResponse, PostGoogleLoginRequest>(
    `${authController}/google`,
    payload,
  )
}

export const ERROR_CODES = {
  LOGIN: 333,
  USER_EXISTS: 334,
  FILE_SIZE: 335,
  ONLY_IMAGES: 336,
} as const

export interface PostRegisterResponseError {
  err: 'P2002'
  message: string
  code: (typeof ERROR_CODES)[keyof typeof ERROR_CODES]
}

export interface PostRegisterResponseSuccess {
  data: {
    email: string
  }
}

export type PostRegisterResponse
  = | PostRegisterResponseSuccess
    | PostRegisterResponseError

export type PostRegisterPayload = RegisterFormValues

export type PostRegisterQuery = (
  newData: PostRegisterPayload,
) => Promise<PostRegisterResponse>

export async function postRegister(UserPayload: PostRegisterRequest) {
  return postRequest<PostRegisterResponse, PostRegisterRequest>(
    `${authController}/register`,
    UserPayload,
  )
}

export interface PostVerifyResponseSuccess {
  data: {
    email: string
    userName: string
    accessToken: string
    refreshToken: string
    role: string
    permissions: string[]
  }
}

export type PostVerifyResponse = PostVerifyResponseSuccess | APIError

export type PostVerifyPayload = VerifyFormValues

export type PostVerifyQuery = (
  newData: PostVerifyPayload,
) => Promise<PostVerifyResponse>

export async function postVerify(UserPayload: PostVerifyRequest) {
  return postRequest<PostVerifyResponse, PostVerifyRequest>(
    `${authController}/validate`,
    UserPayload,
  )
}

export interface PostResendResponseError {
  err: 'P2002'
  message: string
}

export interface PostResendResponseSuccess {
  ok: boolean
}

export type PostResendResponse
  = | PostResendResponseSuccess
    | PostResendResponseError

export interface PostResendPayload { email: string }

export type PostResendQuery = (
  newData: PostResendPayload,
) => Promise<PostResendResponse>

export async function postResend(UserPayload: PostResendPayload) {
  return postRequest<PostResendResponse, PostResendPayload>(
    `${authController}/resend-code`,
    UserPayload,
  )
}

export interface PostForgotRequest {
  email: string
}

export interface PostForgotResponseSuccess {
  ok: boolean
}

export type PostForgotResponse = PostForgotResponseSuccess | APIError

export interface PostForgotPayload {
  email: string
}

export type PostForgotQuery = (
  newData: PostForgotPayload,
) => Promise<PostForgotResponse>

export async function postForgot(payload: PostForgotRequest) {
  return postRequest<PostForgotResponse, PostForgotRequest>(
    `${authController}/forgot-password`,
    payload,
  )
}

export interface PostResetPasswordRequest {
  code: string
  password: string
}

export interface PostResetPasswordResponseSuccess {
  ok: boolean
}

export type PostResetPasswordResponse
  = | PostResetPasswordResponseSuccess
    | APIError

export type PostResetPasswordQuery = (
  newData: PostResetPasswordRequest,
) => Promise<PostResetPasswordResponse>

export async function postResetPassword(payload: PostResetPasswordRequest) {
  return postRequest<PostResetPasswordResponse, PostResetPasswordRequest>(
    `${authController}/reset-password`,
    payload,
  )
}

export interface GetRefreshTokenResponseSuccess {
  data: {
    email: string
    userName: string
    accessToken: string
    refreshToken: string
    role: string
    permissions: string[]
  }
}

export type GetRefreshTokenResponse = GetRefreshTokenResponseSuccess | APIError

export type GetRefreshTokenQuery = () => Promise<GetRefreshTokenResponse>

export async function getRefreshToken() {
  return getRequest<GetRefreshTokenResponse>('/auth/refresh')
}

export interface GetUserInfoResponseSuccess {
  data: {
    email: string
    userName: string
    role: string
    permissions: string[]
  }
}

export type GetUserInfoResponse = GetUserInfoResponseSuccess | APIError

export type GetUserInfoQuery = () => Promise<GetUserInfoResponse>

export async function getUserInfo() {
  return getRequest<GetUserInfoResponse>(`${authController}/user-info`)
}

// Anonymous Token Types
export interface PostAnonymousTokenResponseSuccess {
  data: {
    anonymousToken: string
    role: string
  }
}

export type PostAnonymousTokenResponse
  = | PostAnonymousTokenResponseSuccess
    | APIError

export type PostAnonymousTokenQuery = () => Promise<PostAnonymousTokenResponse>

export async function postAnonymousToken() {
  return postRequest<PostAnonymousTokenResponse, {}>(
    `${authController}/anonymous-token`,
    {},
  )
}
